import React, { useEffect, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Switch,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import EditIcon from "@mui/icons-material/Edit";
import { visuallyHidden } from "@mui/utils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import "@fontsource/roboto/500.css";

import axiosClient from "../axiosClient";
import { useStateContext } from "../context/ContextProvider";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "name",
    numeric: false,
    label: "Description",
  },
  {
    id: "code",
    numeric: false,
    label: "Barcode",
  },
  {
    id: "category",
    numeric: false,
    label: "Category",
  },
  {
    id: "group",
    numeric: false,
    label: "Group",
  },
  {
    id: "price_1",
    numeric: true,
    label: "Price 1",
  },
  {
    id: "vat_1",
    numeric: true,
    label: "VAT 1",
  },
  {
    id: "price_2",
    numeric: true,
    label: "Price 2",
  },
  {
    id: "vat_2",
    numeric: true,
    label: "VAT 2",
  },
  {
    id: "cost_price",
    numeric: true,
    label: "Cost price",
  },
  {
    id: "active",
    numeric: false,
    label: "Active",
  },
  {
    id: "discountable",
    numeric: false,
    label: "Discountable",
  },
  {
    id: "type",
    numeric: false,
    label: "Type",
  },
  {
    id: "edit",
    numeric: false,
    label: "Edit",
  },
  {
    id: "duplicate",
    numeric: false,
    label: "Duplicate",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            style={{ padding: "4px" }}
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function EnhancedTableToolbar(props) {
  const { numSelected } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <>
          <Typography
            sx={{ flex: "1 1 100%" }}
            variant="h6"
            id="tableTitle"
            component="div"
          >
            Products
          </Typography>
          <TextField
            label="Search"
            sx={{ marginRight: 8 }}
            size="small"
            value={props.searchQuery}
            onChange={(e) => props.setSearchQuery(e.target.value)}
          />
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => {
              props.setShowProductForm(true);
              props.setProductDetails({});
            }}
          >
            add product
          </Button>
        </>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

export default function ProductsTable({
  setShowProductForm,
  setProductDetails,
}) {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const { store } = useStateContext();

  const filteredData = rows.filter(
    (item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.Category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.Group.name.toLowerCase().includes(searchQuery.toLowerCase()) 
  );

  useEffect(() => {
    const correctStoreName = JSON.parse(store).name.replace(/[^a-zA-Z]/g, "");
    const databaseStoreName = `DB301_${
      JSON.parse(store).id
    }_${correctStoreName}`;

    axiosClient
      .get(`products/${databaseStoreName}`)
      .then((response) => {
        setRows(response.data.data);
      })
      .catch((err) => console.log(err));
  }, [store]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(filteredData, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, filteredData]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <EnhancedTableToolbar
          setShowProductForm={setShowProductForm}
          setProductDetails={setProductDetails}
          setSearchQuery={setSearchQuery}
          searchQuery={searchQuery}
        />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={"medium"}
            disableselectiononclick="true"
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      {row.name}
                    </TableCell>
                    <TableCell>{row.code}</TableCell>
                    <TableCell>{row.Category.name}</TableCell>
                    <TableCell>{row.Group.name}</TableCell>
                    <TableCell>{parseFloat(row.price_one).toFixed(2)}</TableCell>
                    <TableCell>{`${row.Vats[0]?.vat}%`}</TableCell>
                    <TableCell>{parseFloat(row.price_two).toFixed(2)}</TableCell>
                    <TableCell>
                      {row.Vats[1] === undefined
                        ? `${row.Vats[0]?.vat}%`
                        : `${row.Vats[1]?.vat}%`}
                    </TableCell>
                    <TableCell>{parseFloat(row.cost_price).toFixed(2)}</TableCell>
                    <TableCell>
                      <Switch
                        defaultChecked={row.active}
                        onClick={() => {
                          const correctStoreName = JSON.parse(
                            store
                          ).name.replace(/[^a-zA-Z]/g, "");
                          const databaseStoreName = `DB301_${
                            JSON.parse(store).id
                          }_${correctStoreName}`;

                          axiosClient.patch("products/product/disen", {
                            params: {
                              productId: row.id,
                              active: !row.active,
                              shopName: databaseStoreName,
                            },
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        defaultChecked={row.discountable}
                        onClick={() => {
                          const correctStoreName = JSON.parse(
                            store
                          ).name.replace(/[^a-zA-Z]/g, "");
                          const databaseStoreName = `DB301_${
                            JSON.parse(store).id
                          }_${correctStoreName}`;

                          axiosClient.patch("products/product/discnon", {
                            params: {
                              productId: row.id,
                              discountable: !row.discountable,
                              shopName: databaseStoreName,
                            },
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell
                      onClick={() => {
                        setShowProductForm(true);
                        setProductDetails(row);
                      }}
                    >
                      <EditIcon />
                    </TableCell>
                    <TableCell
                      onClick={() => {
                        const correctStoreName = JSON.parse(store).name.replace(
                          /[^a-zA-Z]/g,
                          ""
                        );
                        const databaseStoreName = `DB301_${
                          JSON.parse(store).id
                        }_${correctStoreName}`;

                        axiosClient
                          .get(`products/getBarcode/${databaseStoreName}`)
                          .then((response) => {
                            const productData = row;
                        productData.name = "";
                        productData.code = "";
                        productData.cost_price = 0;
                        productData.duplicate = true;
                        productData.code = response.data.barcode;
                        setShowProductForm(true);
                        setProductDetails(productData);
                        });
                      }}
                    >
                      <ContentCopyIcon />
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 33 * emptyRows,
                  }}
                >
                  <TableCell colSpan={13} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
